import * as zExt from "@redotech/rpc/ext";
import * as z from "zod/v4";
import {
  Product2AttributeSchema,
  Product2AttributeValueUpsertSchema,
} from "./product2-attribute-types";

/* Product2Variant */
export const Product2VariantSchema_ = z.object({
  _id: zExt.objectId(),
  attributeValues: z.array(zExt.objectId()),
  images: z.array(zExt.objectId()),
  sku: z.string(),
  upc: z.string().nullish(),
  redoBarcode: z.string().nullish(),
  merchantBarcode: z.string().nullish(),
  price: z.number().nullish(),
  externalId: z.string().nullish(),
  externalSource: z.string().nullish(),
  sellWhenOutOfStock: z.boolean().optional(),
});
export const Product2VariantSchema: z.ZodType<Product2Variant> =
  Product2VariantSchema_;
export interface Product2Variant
  extends z.infer<typeof Product2VariantSchema_> {}

export const Product2VariantUpsertSchema_ = Product2VariantSchema_.extend({
  _id: zExt.objectId().optional(),
});
export const Product2VariantUpsertSchema: z.ZodType<Product2VariantUpsert> =
  Product2VariantUpsertSchema_;
export type Product2VariantUpsert = z.infer<
  typeof Product2VariantUpsertSchema_
>;

const Product2VariantWithAttributeValuesUpsertSchema_ =
  Product2VariantSchema_.extend({
    _id: zExt.objectId().optional(),
    attributeValues: z.array(Product2AttributeValueUpsertSchema),
  });
export const Product2VariantWithAttributeValuesUpsertSchema: z.ZodType<Product2VariantWithAttributeValuesUpsert> =
  Product2VariantWithAttributeValuesUpsertSchema_;
export type Product2VariantWithAttributeValuesUpsert = z.infer<
  typeof Product2VariantWithAttributeValuesUpsertSchema_
>;

/* Product2Status */
const Product2StatusSchema_ = z.enum([
  "ACTIVE",
  "DRAFT",
  "DELETED",
  "ARCHIVED",
]);
export const Product2StatusSchema: z.ZodType<Product2Status> =
  Product2StatusSchema_;
export type Product2Status = z.infer<typeof Product2StatusSchema_>;

/* Product2Image */
const Product2ImageSchema_ = z.object({
  _id: zExt.objectId(),
  url: z.string(),
  altText: z.string().optional(),
});
export const Product2ImageSchema: z.ZodType<Product2Image> =
  Product2ImageSchema_;
export type Product2Image = z.infer<typeof Product2ImageSchema_>;

const Product2ImageUpsertSchema_ = Product2ImageSchema_.extend({
  _id: zExt.objectId().optional(),
});
export const Product2ImageUpsertSchema: z.ZodType<Product2ImageUpsert> =
  Product2ImageUpsertSchema_;
export interface Product2ImageUpsert
  extends z.infer<typeof Product2ImageUpsertSchema_> {}

const Product2ImageCreateSchema_ = Product2ImageSchema_.omit({ _id: true });
export const Product2ImageCreateSchema: z.ZodType<Product2ImageCreate> =
  Product2ImageCreateSchema_;
export type Product2ImageCreate = z.infer<typeof Product2ImageCreateSchema_>;

/* Product2 */
export const Product2Schema_ = z.object({
  _id: zExt.objectId(),
  name: z.string(),
  description: z.string().optional(),
  attributes: z.array(zExt.objectId()),
  vendor: z.string(),
  team: z.object({ _id: zExt.objectId(), name: z.string() }),
  consigner: zExt.objectId().nullish(),
  hsCode: z.string().optional(),
  externalId: z.string().optional(),
  externalSource: z.string().optional(),
  images: z.array(Product2ImageSchema),
  status: Product2StatusSchema.default("ACTIVE"),
  variants: z.array(Product2VariantSchema),
  pdpScrapeAnalysis: z.string().optional(),
  pdpScrapedAt: z.date().optional(),
  createdAt: z.date(),
  updatedAt: z.date(),
});
export const Product2Schema: z.ZodType<Product2> = Product2Schema_;
export interface Product2 extends z.infer<typeof Product2Schema_> {}

const Product2WithAttributesSchema_ = Product2Schema_.extend({
  attributes: z.array(Product2AttributeSchema),
});
export const Product2WithAttributesSchema: z.ZodType<Product2WithAttributes> =
  Product2WithAttributesSchema_;
export interface Product2WithAttributes
  extends z.infer<typeof Product2WithAttributesSchema_> {}

export const Product2UpsertSchema_ = Product2Schema_.extend({
  _id: zExt.objectId().optional(),
  createdAt: z.date().optional(),
  updatedAt: z.date().optional(),
  images: z.array(Product2ImageUpsertSchema),
  variants: z.array(Product2VariantUpsertSchema),
});

export const Product2UpsertSchema: z.ZodType<Product2Upsert> =
  Product2UpsertSchema_;
export type Product2Upsert = z.infer<typeof Product2UpsertSchema_>;
